import { useState } from 'react';
import { Mail, Shield, Zap, X } from 'lucide-react';

export default function EmailCaptureModal({ isOpen, onClose, onSubmit }) {
  const [email, setEmail] = useState('');
  const [marketingConsent, setMarketingConsent] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    
    setIsSubmitting(true);
    
    // Get URL parameters first
    const searchParams = new URLSearchParams(window.location.search);
    
    // Track analytics events with UTM parameters
    const utmSource = searchParams.get('utm_source') || 'direct';
    const utmMedium = searchParams.get('utm_medium') || 'organic';
    const utmCampaign = searchParams.get('utm_campaign') || 'no_campaign';
    
    if (typeof window !== 'undefined' && typeof window.trackEvent === 'function') {
      window.trackEvent('email_capture', 'engagement', `demo_access_request_${utmSource}`, 1);
    }
    
    // Enhanced GA4 event with UTM data
    if (typeof gtag !== 'undefined') {
      gtag('event', 'generate_lead', {
        event_category: 'email_capture',
        event_label: 'demo_access_modal',
        source: utmSource,
        medium: utmMedium,
        campaign: utmCampaign,
        value: 1,
        currency: 'USD'
      });
    }
    if (typeof window.trackFacebookEvent === 'function') {
      window.trackFacebookEvent('Lead', {
        content_name: 'Demo Access Request',
        content_category: 'Email Capture',
        value: 1
      });
    }
    
    // Send to backend immediately (don't wait for parent)
    const leadData = {
      email, 
      marketingConsent,
      source: 'demo_quiz',  // Different source than landing page
      timestamp: new Date().toISOString(),
      utm_source: searchParams.get('utm_source'),
      utm_medium: searchParams.get('utm_medium'),
      utm_campaign: searchParams.get('utm_campaign')
    };
    
    // Determine backend URL based on environment
    const backendUrl = window.location.hostname === 'localhost' 
      ? 'http://localhost:3005/api/capture-lead'
      : 'https://quiz-backend-1en6.onrender.com/api/capture-lead';
    
    console.log('📤 Sending lead from modal to backend:', backendUrl);
    
    // Fire and forget - don't block user experience
    fetch(backendUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(leadData),
      keepalive: true
    }).then(response => {
      if (response.ok) {
        console.log('✅ Lead captured successfully from modal');
      } else {
        console.error('❌ Failed to capture lead from modal:', response.statusText);
      }
    }).catch(error => {
      console.error('💥 Failed to save lead from modal:', error);
    });

    // Also notify parent component
    onSubmit(email, marketingConsent);
    
    setIsSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="relative w-full max-w-md mx-4 bg-neutral-900 rounded-2xl border border-white/10 shadow-2xl">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white/60 hover:text-white"
        >
          <X className="w-5 h-5" />
        </button>
        
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-gradient-to-br from-amber-300 via-pink-500 to-fuchsia-600 rounded-xl">
              <Mail className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-xl font-bold text-white">
              Get Your Demo Access
            </h2>
          </div>
          
          <p className="text-white/70 mb-6">
            Enter your email to access the demo quiz. We'll send you a welcome message with helpful preparation tips.
          </p>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-white/80 mb-2">
                Email Address
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="w-full px-4 py-3 bg-neutral-800 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-fuchsia-400/50"
              />
            </div>
            
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={marketingConsent}
                onChange={(e) => setMarketingConsent(e.target.checked)}
                className="mt-1"
              />
              <span className="text-sm text-white/70">
                <strong className="text-white/90">Send me email with Demo access</strong><br/>
                We need your consent to send you the email with access to the Demo simulator. 
                <span className="text-amber-300"> Without this checkbox, you won't receive the Demo access email!</span>
              </span>
            </label>
            
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3 px-6 bg-gradient-to-r from-amber-300 via-pink-500 to-fuchsia-600 text-white font-semibold rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <Zap className="w-5 h-5" />
              {isSubmitting ? 'Redirecting to demo...' : 'Get Demo Access'}
            </button>
          </form>
          
          <div className="mt-4 flex items-center gap-2 text-xs text-white/50">
            <Shield className="w-4 h-4" />
            <span>No spam. Your data is safe with us.</span>
          </div>
        </div>
      </div>
    </div>
  );
}